import React, { useCallback, useEffect, useMemo, useState } from 'react'
import * as Styled from './DeleteSupportTierModal.styled'
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
} from '@material-ui/core'
import ButtonShadow from 'components/ButtonShadow/ButtonShadow'
import { useFetch } from 'hooks'
import {
  deleteSupportTier,
  getNumberOfSupportTierContents,
  handleSupportTierDeletion,
} from 'services/api'
import VisibilitySelector, {
  VisibilityData,
} from 'components/VisibilitySelector/VisibilitySelector'
import { Formik } from 'formik'
import { SupportTier } from 'types'
import { WarningRounded } from '@material-ui/icons'
import { Colors } from 'styles'

enum DeleteSupportStep {
  CLOSED,
  LOADING,
  MIGRATION,
  CONFIRMATION,
}

const defaultVisibility: VisibilityData = {
  exclusiveToSupporters: false,
  minSupportTierId: null,
  supportTiersIds: null,
}

interface DeleteSupportTierModalProps {
  handleClose: () => void
  tierIdToDelete?: string
  podcastId: string
  tiers?: SupportTier[]
  setSuccessMessage: (message: string) => void
  setErrorMessage: (message: string) => void
  fetchData: () => Promise<void>
}

const DeleteSupportTierModal: React.FC<DeleteSupportTierModalProps> = ({
  handleClose,
  tierIdToDelete,
  podcastId,
  tiers,
  setSuccessMessage,
  setErrorMessage,
  fetchData,
}) => {
  const [step, setStep] = useState<DeleteSupportStep>(DeleteSupportStep.CLOSED)
  const [visibilityData, setVisibilityData] =
    useState<Partial<VisibilityData> | null>(null)

  const { data: tierContentCount, fetchData: fetchTierContentCount } =
    useFetch<{
      contentCount: number
    }>(
      useCallback(
        async (supportTierId) => {
          await new Promise((resolve) => setTimeout(resolve, 1000))
          return getNumberOfSupportTierContents(podcastId, supportTierId)
        },
        [podcastId],
      ),
      false,
    )

  useEffect(() => {
    if (!tierIdToDelete) {
      setStep(DeleteSupportStep.CLOSED)
      setVisibilityData(null)
      return
    }

    setStep(DeleteSupportStep.LOADING)
    fetchTierContentCount(tierIdToDelete)
  }, [fetchTierContentCount, tierIdToDelete])

  const hasContent = useMemo(
    () => (tierContentCount ? tierContentCount.contentCount > 0 : undefined),
    [tierContentCount],
  )

  const handleDeleteTier = useCallback(async () => {
    setStep(DeleteSupportStep.LOADING)

    if (hasContent) {
      const result = await handleSupportTierDeletion(podcastId, {
        deletedSupportTierId: tierIdToDelete,
        cumulativeMigration: visibilityData as VisibilityData,
      })

      if (result.error) {
        setErrorMessage('Ocorreu um erro ao migrar conteúdos vinculados')
        handleClose()
        return
      }
    }

    const result = await deleteSupportTier(podcastId, tierIdToDelete)

    if (result.error) {
      setErrorMessage('Ocorreu um erro ao deletar o nível de apoio')
      handleClose()
      return
    }

    setSuccessMessage('Nível de apoio removido com sucesso!')
    handleClose()
    fetchData()
  }, [
    podcastId,
    tierIdToDelete,
    hasContent,
    handleClose,
    visibilityData,
    fetchData,
    setSuccessMessage,
    setErrorMessage,
  ])

  useEffect(() => {
    if (hasContent === undefined) return
    setStep(
      hasContent ? DeleteSupportStep.MIGRATION : DeleteSupportStep.CONFIRMATION,
    )
  }, [hasContent])

  const allowedTiers = useMemo(
    () => tiers?.filter((tier) => tier.id !== tierIdToDelete) ?? [],
    [tiers, tierIdToDelete],
  )
  const deletedTier = useMemo(
    () => tiers?.find((tier) => tier.id === tierIdToDelete),
    [tiers, tierIdToDelete],
  )

  return (
    <>
      <LoadingModal
        isOpen={step === DeleteSupportStep.LOADING}
        handleClose={() => {}}
      />

      <TierMigrationModal
        isOpen={step === DeleteSupportStep.MIGRATION}
        handleClose={handleClose}
        allowedTiers={allowedTiers}
        setVisibilityData={setVisibilityData}
        setStep={setStep}
      />

      <ConfirmationModal
        isOpen={step === DeleteSupportStep.CONFIRMATION}
        handleClose={handleClose}
        deletedTier={deletedTier}
        visibilityData={visibilityData}
        tiers={tiers}
        handleDeleteTier={handleDeleteTier}
      />
    </>
  )
}

interface BaseProps {
  isOpen: boolean
  handleClose: () => void
}

const LoadingModal: React.FC<BaseProps> = ({ isOpen, handleClose }) => {
  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogContent>
        <Styled.LoadingContainer>
          <CircularProgress />
          <Styled.LoadingText>Carregando...</Styled.LoadingText>
        </Styled.LoadingContainer>
      </DialogContent>
    </Dialog>
  )
}

interface TierMigrationModalProps {
  allowedTiers: SupportTier[]
  setVisibilityData: (data: Partial<VisibilityData>) => void
  setStep: (step: DeleteSupportStep) => void
}

const TierMigrationModal: React.FC<BaseProps & TierMigrationModalProps> = ({
  isOpen,
  handleClose,
  allowedTiers,
  setVisibilityData,
  setStep,
}) => {
  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogContent>
        <Styled.Container>
          <Styled.Title>
            Você possui conteúdo vinculado nesse nível
          </Styled.Title>
          <Styled.Info>
            Para continuar com a deleção decida a nova visibilidade para os
            conteúdos vinculados com nível cumulativo.
          </Styled.Info>
          {/* Formik used only to allow using SelectField component that uses "useField" */}
          <Formik initialValues={{}} onSubmit={() => {}}>
            <VisibilitySelector
              tiers={allowedTiers}
              initialValues={defaultVisibility}
              onChangeData={setVisibilityData}
            />
          </Formik>
        </Styled.Container>
      </DialogContent>
      <DialogActions style={{ gap: 20, padding: 20 }}>
        <ButtonShadow
          size="regular"
          variant="secondary"
          label="Cancelar"
          onPress={handleClose}
        />
        <ButtonShadow
          size="regular"
          label="Continuar"
          onPress={() => setStep(DeleteSupportStep.CONFIRMATION)}
        />
      </DialogActions>
    </Dialog>
  )
}

function getTierLabel(tier?: SupportTier): string {
  return `"${tier.title ?? ''}" (R$ ${tier.value ?? ''})`
}

interface ConfirmationModalProps {
  deletedTier?: SupportTier
  tiers: SupportTier[]
  visibilityData: Partial<VisibilityData> | null
  handleDeleteTier: () => void
}

const ConfirmationModal: React.FC<BaseProps & ConfirmationModalProps> = ({
  isOpen,
  handleClose,
  deletedTier,
  visibilityData,
  tiers,
  handleDeleteTier,
}) => {
  const tierMigrationSummary = useMemo(() => {
    if (!visibilityData) {
      return ''
    }

    if (!visibilityData.exclusiveToSupporters) {
      return 'O conteúdo vinculado com nível cumulativo ficará público.'
    }

    if (visibilityData.minSupportTierId) {
      const minSupportTier = tiers.find(
        (tier) => tier.id === visibilityData.minSupportTierId,
      )
      return `O conteúdo vinculado com nível cumulativo ficará disponível apenas para apoiadores a partir do nível ${getTierLabel(
        minSupportTier,
      )}.`
    }

    if (visibilityData.supportTiersIds) {
      const supportTiers = tiers.filter((tier) =>
        visibilityData.supportTiersIds.includes(tier.id),
      )
      const supportTiersLabel = supportTiers.map(getTierLabel)
      return `O conteúdo vinculado com nível cumulativo ficará disponível apenas para apoiadores dos níveis: ${supportTiersLabel.join(
        ', ',
      )}.`
    }

    return 'O conteúdo vinculado com nível cumulativo ficará disponível para todos apoiadores.'
  }, [visibilityData, tiers])

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogContent>
        <Styled.Container>
          <Styled.Title>Confirme a deleção</Styled.Title>
          {visibilityData && (
            <Styled.Info>
              Ao continuar:
              <ul>
                <li>{tierMigrationSummary}</li>
                <li>
                  O conteúdo vinculado com níveis fixos deixará de fazer parte
                  do nível "{deletedTier?.title}" (R$ {deletedTier?.value}).
                </li>
                <li>
                  O nível "{deletedTier?.title}" (R$ {deletedTier?.value}) será
                  apagado permanentemente.
                </li>
              </ul>
            </Styled.Info>
          )}
          <Styled.WarningContainer>
            <WarningRounded fontSize="medium" style={{ color: Colors.BLACK }} />
            <Styled.WarningText>
              Essa ação não pode ser desfeita
            </Styled.WarningText>
          </Styled.WarningContainer>
        </Styled.Container>
      </DialogContent>
      <DialogActions style={{ gap: 20, padding: 20 }}>
        <ButtonShadow
          size="regular"
          variant="secondary"
          label="Cancelar"
          onPress={handleClose}
        />
        <ButtonShadow
          size="regular"
          label="Apagar"
          onPress={handleDeleteTier}
        />
      </DialogActions>
    </Dialog>
  )
}

export default DeleteSupportTierModal
